"use client";

import React, { Component, ReactNode } from "react";
import { ErrorState } from "./error-state";
import {
    getDefaultErrorForContext,
    getSafeErrorMessage,
    type ErrorContext,
    type SafeErrorMessage,
} from "@/lib/safe-error";

interface Props {
    children: ReactNode;
    /** Maps thrown errors to safe user-facing copy */
    context?: ErrorContext;
    /**
     * Custom error UI. Pass `null` to swallow the error silently
     * (e.g. optional SDO prefetch). Omitting the prop shows ErrorState.
     */
    fallback?: ReactNode;
    variant?: "inline" | "card" | "panel" | "page";
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    onRetry?: () => void;
}

interface State {
    hasError: boolean;
    message: SafeErrorMessage | null;
}

/**
 * True when a custom fallback was provided — including `null`, which means
 * "render nothing". A truthy check would treat `null` as missing and show
 * the generic ErrorState instead.
 */
export function hasCustomErrorFallback(fallback: ReactNode | undefined): boolean {
    return fallback !== undefined;
}

/**
 * Catches Convex query errors and other React render errors,
 * showing polished UI instead of crashing or exposing backend messages.
 */
export class QueryErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, message: null };
    }

    static getDerivedStateFromError(): State {
        return { hasError: true, message: null };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({
            message: getSafeErrorMessage(error, this.props.context ?? "generic"),
        });
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.children !== this.props.children && this.state.hasError) {
            this.setState({ hasError: false, message: null });
        }
    }

    handleRetry = () => {
        this.setState({ hasError: false, message: null });
        this.props.onRetry?.();
    };

    render() {
        if (this.state.hasError) {
            if (hasCustomErrorFallback(this.props.fallback)) {
                return this.props.fallback;
            }

            const message =
                this.state.message ??
                getDefaultErrorForContext(this.props.context ?? "generic");

            return (
                <ErrorState
                    variant={this.props.variant ?? "card"}
                    title={message.title}
                    description={message.description}
                    canRetry={message.canRetry}
                    isOffline={message.isOffline}
                    onRetry={this.handleRetry}
                />
            );
        }

        return this.props.children;
    }
}
